/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { Checkbox } from '@strata/react-checkbox-list';
import { FormSection } from '@strata/react-form-section';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { NumberInputFormik } from '@strata/react-inputs';
import { SimpleSelect, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import {
    deleteMessagesFromDeadLetterQueue,
    getMessagesFromDeadLetterQueue,
    type ServiceBusMessage,
} from 'api/oopsAPI';
import { DeadLetterMessagesGrid } from 'components/serviceBus/DeadLetterMessagesGrid';
import { MessagePreviewModal } from 'components/serviceBus/MessagePreviewModal';
import { handleError } from 'services/Error';
import { useGetTopicsAndSubscriptionsQuery } from 'store/api/oopsApi';

dayjs.extend(localizedFormat);

const Actions = {
    DELETE: 'Delete',
    REPLAY: 'Replay',
} as const;

const PastActionMap = {
    [Actions.DELETE]: 'deleted',
    [Actions.REPLAY]: 'replayed',
};

const ValidationSchema = Yup.object({
    topic: Yup.string().required(validationStrings.requiredField.errorMessage),
    onlyDeadTopics: Yup.bool().required(validationStrings.requiredField.errorMessage),
    subscription: Yup.string().required(validationStrings.requiredField.errorMessage),
    onlyDeadSubs: Yup.bool().required(validationStrings.requiredField.errorMessage),
    maxCount: Yup.number()
        .min(1, 'Must be greater than zero.')
        .max(500, 'Must not be greater than 500.')
        .required(validationStrings.requiredField.errorMessage),
    editCount: Yup.number()
        .min(1, 'Must be greater than zero.')
        .max(500, 'Must not be greater than 500.')
        .required(validationStrings.requiredField.errorMessage),
    action: Yup.string()
        .oneOf(Object.values(Actions))
        .required(validationStrings.requiredField.errorMessage),
});

export const ServiceBus = () => {
    const {
        data: topics,
        isFetching: topicsFetching,
        fulfilledTimeStamp: topicsFulfilledTimestamp,
        refetch: refetchTopics,
    } = useGetTopicsAndSubscriptionsQuery();
    const [lastMaxSequenceNumber, setLastMaxSequenceNumber] = useState<number>(0);
    const [messages, setMessages] = useState({});
    const [messagesFetching, setMessagesFetching] = useState(false);
    const [messagesFulfilledTimestamp, setMessagesFulfilledTimestamp] = useState<string>();
    const [selectedMessage, setSelectedMessage] = useState<ServiceBusMessage>();

    const resetMessagesState = () => {
        setMessages({});
        setLastMaxSequenceNumber(0);
        setMessagesFulfilledTimestamp(undefined);
    };

    const getMessages = async (values: Yup.InferType<typeof ValidationSchema>, next = false) => {
        try {
            setMessagesFetching(true);
            const data = await getMessagesFromDeadLetterQueue({
                topic: values.topic,
                subscription: values.subscription,
                maxCount: values.maxCount,
                minSequenceNumber: next ? lastMaxSequenceNumber : 0,
            });
            const newMessages = data.messages?.reduce(
                (acc, message) => ({ ...acc, [message.id]: message }),
                {},
            );
            setMessages((current) => (next ? { ...current, ...newMessages } : newMessages));
            setLastMaxSequenceNumber(data.max_sequence_number);
            setMessagesFulfilledTimestamp(dayjs().toISOString());
        } catch (err) {
            handleError('Unable to retrieve messages from queue', err as Error);
        }
        setMessagesFetching(false);
    };

    return (
        <Formik
            initialValues={
                {
                    onlyDeadTopics: false,
                    onlyDeadSubs: false,
                    maxCount: 10,
                    action: Actions.REPLAY,
                } as Yup.InferType<typeof ValidationSchema>
            }
            validationSchema={ValidationSchema}
            validateOnMount
            onSubmit={async (values, { setFieldValue }) => {
                try {
                    await deleteMessagesFromDeadLetterQueue({
                        topic: values.topic,
                        subscription: values.subscription,
                        count: values.editCount,
                        replay: values.action === Actions.REPLAY,
                    });
                    await setFieldValue('editCount', undefined);
                    await refetchTopics();
                    void getMessages(values);
                    SuccessToast({
                        message: `Successfully ${PastActionMap[values.action]} ${values.editCount} messages.`,
                    });
                } catch (err) {
                    handleError(
                        `Unable to ${values.action.toLocaleLowerCase()} ${values.editCount} messages.`,
                        err as Error,
                    );
                }
            }}
        >
            {(formik) => {
                const { topic, subscription, action, onlyDeadTopics, onlyDeadSubs } = formik.values;
                const { editCount: editCountError, ...queueErrors } = formik.errors;

                const selectedTopic = topics?.find(({ name }) => name === topic);
                const selectedSubscription = selectedTopic?.subscriptions.find(
                    ({ name }) => name === subscription,
                );

                const disableFetch = messagesFetching || Object.keys(queueErrors).length > 0;

                return (
                    <Form style={{ height: '100%' }}>
                        <FullscreenLoading
                            isShowing={formik.isSubmitting}
                            loadingText="Attempting queue action..."
                        />
                        <MessagePreviewModal
                            message={selectedMessage}
                            onClose={() => setSelectedMessage(undefined)}
                        />
                        <FormSection
                            sectionTitle="Manage Dead-letter Queues"
                            className="sds_service-bus"
                            isLoading={topicsFetching}
                            rightHeaderContent={
                                <div className="flex__gap--medium" style={{ alignItems: 'center' }}>
                                    {topicsFulfilledTimestamp && (
                                        <span>
                                            {`Last Fetched: ${dayjs(topicsFulfilledTimestamp).format('L LTS')}`}
                                        </span>
                                    )}
                                    <ActionButtonText
                                        label="Reload Topics"
                                        leftIcon={IconNames.REFRESH}
                                        disabled={topicsFetching}
                                        onClick={() => {
                                            void refetchTopics();
                                        }}
                                    />
                                </div>
                            }
                            content={
                                <>
                                    <div
                                        className="flex__gap--large"
                                        style={{ alignItems: 'center' }}
                                    >
                                        <div style={{ flexGrow: 1 }}>
                                            <SuggestSingleSelectFormik
                                                id="topic"
                                                label="Topic"
                                                required
                                                isLoading={topicsFetching}
                                                values={topics?.filter((busTopic) =>
                                                    onlyDeadTopics ? busTopic.deadCount > 0 : true,
                                                )}
                                                selectedItem={selectedTopic}
                                                onChange={(newValue) => {
                                                    void (async () => {
                                                        await formik.setFieldValue(
                                                            'topic',
                                                            newValue?.name,
                                                        );
                                                        await formik.setFieldValue(
                                                            'subscription',
                                                            undefined,
                                                        );
                                                        await formik.setFieldValue(
                                                            'editCount',
                                                            undefined,
                                                        );
                                                        resetMessagesState();
                                                    })();
                                                }}
                                            />
                                        </div>
                                        <Checkbox
                                            name="onlyDeadTopics"
                                            label="Only with DeadLetter > 0"
                                            className="padding__top--medium"
                                            checked={onlyDeadTopics}
                                            onChange={(newValue) => {
                                                void formik.setFieldValue(
                                                    'onlyDeadTopics',
                                                    newValue,
                                                );
                                            }}
                                        />
                                    </div>
                                    <div
                                        className="flex__gap--large"
                                        style={{ alignItems: 'center' }}
                                    >
                                        <div style={{ flexGrow: 1 }}>
                                            <SuggestSingleSelectFormik
                                                id="subscription"
                                                label="Subscription"
                                                required
                                                disabled={!topic}
                                                values={selectedTopic?.subscriptions.filter(
                                                    (topicSubscription) =>
                                                        onlyDeadTopics || onlyDeadSubs
                                                            ? topicSubscription.deadCount > 0
                                                            : true,
                                                )}
                                                selectedItem={selectedSubscription}
                                                onChange={(newValue) => {
                                                    void (async () => {
                                                        await formik.setFieldValue(
                                                            'subscription',
                                                            newValue?.name,
                                                        );
                                                        await formik.setFieldValue(
                                                            'editCount',
                                                            undefined,
                                                        );
                                                        resetMessagesState();
                                                    })();
                                                }}
                                            />
                                        </div>
                                        <Checkbox
                                            name="onlyDeadSubs"
                                            label="Only with DeadLetter > 0"
                                            className="padding__top--medium"
                                            disabled={onlyDeadTopics}
                                            checked={onlyDeadTopics || onlyDeadSubs}
                                            onChange={(newValue) => {
                                                void formik.setFieldValue('onlyDeadSubs', newValue);
                                            }}
                                        />
                                    </div>
                                    <NumberInputFormik
                                        id="maxCount"
                                        label="Max Count"
                                        required
                                        step={10}
                                        min={10}
                                    />
                                    <div
                                        className="flex__gap--medium padding__bottom--medium"
                                        style={{ alignItems: 'center' }}
                                    >
                                        <Button
                                            label={
                                                messagesFulfilledTimestamp
                                                    ? 'Refetch'
                                                    : 'Fetch Messages'
                                            }
                                            intent={Button.Intent.PRIMARY}
                                            disabled={disableFetch}
                                            onClick={() => {
                                                void getMessages(formik.values);
                                            }}
                                        />
                                        {messagesFulfilledTimestamp && (
                                            <>
                                                <Button
                                                    label="Fetch More"
                                                    intent={Button.Intent.PRIMARY}
                                                    disabled={
                                                        disableFetch ||
                                                        Object.keys(messages).length >=
                                                            (selectedSubscription?.deadCount ?? 0)
                                                    }
                                                    onClick={() => {
                                                        void getMessages(formik.values, true);
                                                    }}
                                                />
                                                <span>
                                                    {`Last Fetched: ${dayjs(
                                                        messagesFulfilledTimestamp,
                                                    ).format(
                                                        'L LTS',
                                                    )} | Total: ${Object.keys(messages).length}`}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <div
                                        className="flex__gap--medium"
                                        style={{ alignItems: 'flex-start' }}
                                    >
                                        <SimpleSelect
                                            values={Object.values(Actions)}
                                            selectedItem={action}
                                            onChange={(newValue) => {
                                                void formik.setFieldValue('action', newValue);
                                            }}
                                        />
                                        <div className="padding__top--x-small">top</div>
                                        <NumberInputFormik id="editCount" required />
                                        <div className="padding__top--x-small">
                                            messages from dead-letter queue.
                                        </div>
                                        <Button
                                            label="Apply"
                                            type="submit"
                                            intent={Button.Intent.DANGER}
                                            disabled={!formik.isValid}
                                        />
                                    </div>
                                    <DeadLetterMessagesGrid
                                        messagesFetching={messagesFetching}
                                        onRowClick={(value) => setSelectedMessage(value)}
                                        messages={Object.values(messages)}
                                    />
                                </>
                            }
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};
